import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import { useHistory } from 'react-router-dom';
import useCauseNoteService from '../../app/services/hooks/useCauseNoteService';
import { CauseNoteDataForm, CauseNoteFilters } from '../../app/models/CauseNote';
import CauseNoteDataTable from './components/tables/CauseNoteDataTable';
import CauseNoteCreate from './components/CauseNoteCreate';
import CauseNoteEdit from './components/CauseNoteEdit';
import useNavigationPage from '../../hooks/useNavigationPage';


const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'motivos de notas',
        url: '/cause-notes',
        isActive: true
    }
];

const CauseNotes = () => {
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetCauseNotes, getCauseNotes, deleteCauseNote, activeCauseNote } =
        useCauseNoteService();
    const [causeNotes, setCauseNotes] = useState<CauseNoteDataForm[]>([]);

    const [causeNoteIdEdit, setCauseNoteIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [causeNoteFilter, setCauseNoteFilter] = useState<CauseNoteFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'asc'
    });

    useEffect(() => {
        reloadTable();
    }, [causeNoteFilter]);

    const getAllCauseNote = () => {
        getCauseNotes(causeNoteFilter, {
            onSuccess: (response: ServiceResponse) => {
                setCauseNotes(response.data.cause_notes);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (causeNoteId: number) => {
        setShowingEdit(true);
        setCauseNoteIdEdit(causeNoteId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setCauseNoteIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllCauseNote();
    };

    const destroy = (causeNoteId: number) => {
        const _text = 'Está a punto de eliminar el motivo de nota #' + causeNoteId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando motivo de nota...');
                        deleteCauseNote(causeNoteId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'EL motivo de nota no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };


    return (
        <>
            <Breadcrumbs pageSection="Motivos de Notas" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo motivo de notas" />}
                />
                <CauseNoteDataTable
                    causeNotes={causeNotes}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetCauseNotes}
                    filter={causeNoteFilter}
                    setFilter={setCauseNoteFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear motivo de notas"
                    backdrop={true}
                    showFooter={false}
                >
                    <CauseNoteCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar motivo de nota"
                    backdrop={true}
                    showFooter={false}
                >
                    <CauseNoteEdit
                        causeNoteId={causeNoteIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default CauseNotes;
